import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAwards, listDeferredWebhooks, listWebhooks, shareHighlight, sharePlayerOfRound } from '../api/portal';
import { listHighlights } from '../api/clips';
import { ApiError } from '../api/client';

export function SocialDeskPage() {
  const qc = useQueryClient();
  const hooks = useQuery({ queryKey: ['portal-webhooks'], queryFn: listWebhooks });
  const videos = useQuery({ queryKey: ['highlights'], queryFn: listHighlights });
  const awards = useQuery({ queryKey: ['portal-awards'], queryFn: listAwards });
  const deferred = useQuery({ queryKey: ['portal-deferred'], queryFn: listDeferredWebhooks, refetchInterval: 15_000 });
  const social = (hooks.data ?? []).filter((h) =>
    ['telegram', 'instagram', 'facebook', 'whatsapp'].includes(h.kind ?? '') && h.is_active);
  const ready = (videos.data ?? []).filter((v) =>
    v.render_status === 'ready' && (v.review_status ?? 'published') === 'published');
  const [hookId, setHookId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const share = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: 'now' | 'morning' }) =>
      shareHighlight(id, { webhook_id: hookId || undefined, mode }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-deferred'] });
      qc.invalidateQueries({ queryKey: ['portal-deliveries'] });
      setMsg(res.mode === 'morning'
        ? `Zakazano za ${res.release_at?.replace('T', ' ').slice(0, 16) ?? 'jutro'}`
        : (res.share_url
          ? `Caption spreman. Otvori: ${res.share_url}`
          : (res.ok === false ? (res.error || 'Destinacija nije primila') : 'Poslato')));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Social</div>
        <h1 className="page-title">Social desk</h1>
        <p className="page-sub">
          Portal kači objavljeni reel na Telegram, Instagram, Facebook ili WhatsApp.
          „Sada“ ide odmah; „Jutro“ čeka morning pack sat. Bez Meta tokena dobijaš caption + share link.
        </p>
      </div>
      {social.length === 0 && (
        <div className="card mb-4 p-3 text-sm text-slate-300">
          Nema social destinacije.{' '}
          <Link to="/webhooks" className="text-white underline">Dodaj telegram: / instagram: / facebook: / whatsapp:</Link>
        </div>
      )}
      {social.length > 0 && (
        <div className="mb-4 max-w-md">
          <label className="label">Destinacija</label>
          <select className="input" value={hookId} onChange={(e) => setHookId(e.target.value)}>
            <option value="">Prva social</option>
            {social.map((h) => <option key={h.id} value={h.id}>{h.kind} · {h.url}</option>)}
          </select>
        </div>
      )}
      {msg && <div className="card mb-4 p-3 text-sm text-slate-200">{msg}</div>}
      {(awards.data ?? []).length > 0 && (
        <div className="card mb-4 p-4">
          <div className="mb-2 text-[11px] uppercase text-slate-500">Igrač kola</div>
          {(awards.data ?? []).map((a) => (
            <div key={a.round} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm">
              <span className="text-white">{a.player} <span className="text-slate-500">· {a.round}</span></span>
              <span className="flex flex-wrap gap-1">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={social.length === 0}
                  onClick={() => sharePlayerOfRound(a.round, { webhook_id: hookId || undefined, mode: 'now' })
                    .then(() => setMsg('Igrač kola poslat.'))
                    .catch((e) => setMsg(e instanceof ApiError ? e.message : String(e)))}
                >
                  Pošalji copy
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={social.length === 0}
                  onClick={() => sharePlayerOfRound(a.round, { webhook_id: hookId || undefined, mode: 'morning' })
                    .then((res) => setMsg(`Zakazano za ${res.release_at?.replace('T', ' ').slice(0, 16) ?? 'jutro'}`))
                    .catch((e) => setMsg(e instanceof ApiError ? e.message : String(e)))}
                >
                  Morning pack
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="card p-4 mb-4">
        <table className="table">
          <thead>
            <tr><th>Reel</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {ready.map((v) => (
              <tr key={v.id}>
                <td className="text-white">{v.name}</td>
                <td className="text-xs text-slate-400">{v.render_status} · {v.review_status ?? 'published'}</td>
                <td className="flex flex-wrap gap-1">
                  <button type="button" className="btn-primary text-xs" disabled={share.isPending || social.length === 0}
                    onClick={() => share.mutate({ id: v.id, mode: 'now' })}>Pošalji sada</button>
                  <button type="button" className="btn-ghost text-xs" disabled={share.isPending || social.length === 0}
                    onClick={() => share.mutate({ id: v.id, mode: 'morning' })}>Zakaži jutro</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ready.length === 0 && <div className="py-6 text-center text-sm text-slate-500">Nema objavljenih reels.</div>}
      </div>
      {(deferred.data ?? []).length > 0 && (
        <div className="card p-4">
          <div className="mb-2 text-[11px] uppercase text-slate-500">Čeka morning pack</div>
          {(deferred.data ?? []).map((d) => (
            <div key={d.id} className="flex justify-between text-sm py-1">
              <span className="text-slate-300">{d.event_type}</span>
              <span className="text-xs text-slate-500">{d.release_at.replace('T', ' ').slice(0, 16)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
